import Clock from "./clock";

const defaultTimeProvider = () => Date.now();

/**
 * Delta-computed game clock. Ticks via wall-clock time differences
 * when not paused. Persistable across page reload because referenceTime
 * is a wall-clock epoch (Date.now()).
 *
 * State persisted:
 *  - referenceValue (ms): the clock value at the last pause/setSeconds anchor
 *  - referenceTime (epoch ms or null): wall-clock anchor when running
 *  - paused (bool): whether the clock is advancing
 *  - direction ("down" | "up"): count direction
 *  - timeLimitInMilliseconds (ms): period length for countdown bounds
 */
export default class GameClock extends Clock {
    constructor(
        timeLimitInMilliseconds = 0,
        direction = "down",
        timeProvider = defaultTimeProvider
    ) {
        super();

        this.timeLimitInMilliseconds = timeLimitInMilliseconds;
        this.direction = direction;
        this.timeProvider = timeProvider;
        this.referenceValue =
            direction === "down" ? timeLimitInMilliseconds : 0;
        this.referenceTime = null;
        this.paused = true;
    }

    /**
     * Wall-clock timestamp helper. Internal use only.
     * @private
     */
    _wallClockNow() {
        return this.timeProvider();
    }

    /**
     * Return the current effective game time in milliseconds.
     * Same contract as ManualGameClock.now() — callers don't need to know
     * which subclass they're holding.
     */
    now() {
        return this.getCurrentTime();
    }

    start() {
        this.referenceTime = this._wallClockNow();
        this.paused = false;
        return this.referenceTime;
    }

    pause() {
        if (!this.paused) {
            this.referenceValue = this.getCurrentTime();
            this.referenceTime = null;
            this.paused = true;
        }
    }

    isPaused() {
        return this.paused;
    }

    continue() {
        if (this.paused) {
            this.referenceTime = this._wallClockNow();
            this.paused = false;
        }
    }

    reset() {
        this.referenceValue =
            this.direction === "down" ? this.timeLimitInMilliseconds : 0;
        this.referenceTime = null;
        this.paused = true;
    }

    getCurrentTime() {
        if (this.paused || this.referenceTime == null) {
            return this.referenceValue;
        }

        const elapsed = this._wallClockNow() - this.referenceTime;

        if (this.direction === "down") {
            return Math.max(0, this.referenceValue - elapsed);
        }

        return this.referenceValue + elapsed;
    }

    getElapsedTime() {
        const current = this.getCurrentTime();

        if (this.direction === "down") {
            return this.timeLimitInMilliseconds - current;
        }

        return current;
    }

    getRemainingTime() {
        if (this.direction === "down") {
            return this.getCurrentTime();
        }

        return this.timeLimitInMilliseconds
            ? this.timeLimitInMilliseconds - this.getCurrentTime()
            : 0;
    }

    setSeconds(seconds) {
        this.referenceValue = seconds * 1000;
        this.referenceTime = this.paused ? null : this._wallClockNow();
    }

    /**
     * Set the period length the clock measures against. Periods can differ
     * in length (a timed overtime period, PSTATS-1268), so a period start
     * sets it alongside the starting value.
     * @param {number} seconds
     */
    setLimitSeconds(seconds) {
        this.timeLimitInMilliseconds = seconds * 1000;
    }

    /**
     * Change the count direction, keeping the current effective time as the
     * new anchor so the displayed value doesn't jump. No-op when the
     * direction is unchanged.
     * @param {"down" | "up"} direction
     */
    setDirection(direction) {
        if (direction !== this.direction) {
            this.referenceValue = this.getCurrentTime();
            this.referenceTime = this.paused ? null : this._wallClockNow();
            this.direction = direction;
        }
    }

    addSeconds(seconds) {
        this.referenceValue += seconds * 1000;
    }

    /**
     * Exclude timeProvider (a function) from serialization — IndexedDB's
     * structured clone cannot handle functions. Running state (referenceTime
     * + paused: false) IS persisted so the clock can keep ticking across a
     * refresh that happens during an active session. Stale state (long
     * elapsed) is healed in fromJSON.
     */
    toJSON(includedProps = [], excludedProps = []) {
        return super.toJSON(includedProps, [...excludedProps, "timeProvider"]);
    }

    /**
     * Migrate stale persisted running state during revive. If referenceTime
     * is far enough in the past that the clock would have clamped to 0
     * (legacy data, or browser-closed-for-hours), freeze at referenceValue.
     * Normal cross-refresh ticking (small elapsed) is preserved.
     */
    // eslint-disable-next-line class-methods-use-this
    fromJSON(obj) {
        if (obj && obj.referenceTime != null && obj.paused === false) {
            const elapsed = Date.now() - obj.referenceTime;
            // 1 hour: longer than any realistic single browser session
            // for a coach who refreshes/reopens the tab during a game.
            const STALE_THRESHOLD_MS = 60 * 60 * 1000;
            if (elapsed > STALE_THRESHOLD_MS) {
                obj.referenceTime = null;
                obj.paused = true;
                // referenceValue unchanged — clock's value at last anchor.
            }
        }
    }
}
